import { TResponse } from '@r1-frontend/do-request/core/types';
import { captureQueryError } from '@r1-frontend/do-request/error';
import { IAdvertising } from '@r1-frontend/api-domru/api-content/_dto/IAdvertising';
import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';

import { TButtonType } from '@r1-frontend/ui-react/components/buttons/button';

import contentRequest from '~/src/api/requests/contentRequest';

export interface IBannerTag {
    alias: string,
    id: number,
    name: string,
}

export interface IBannerInfo {
    available_auth: number,
    available_new: number,
    button_text: string,
    button_type: TButtonType,
    callback_form: number,
    created_at: string,
    description: string,
    id: number,
    image: string,
    image_webp: string,
    image_background: string,
    image_background_webp: string,
    image_background_mobile: string,
    image_mobile: string,
    is_active: number,
    link: string,
    phone_introduction: number,
    tab_text: string,
    tab_title: string,
    tags: IBannerTag[],
    text: string,
    text_color: string,
    title: string,
    updated_at: string,
    age_limit?: string,
    legal_info?: string,
    legal_info_text_color?: string,
    advertising?: IAdvertising,
}

export interface IBanner {
    banner: number,
    bannerInfo: IBannerInfo,
    city_id: number,
    id: number,
    order: number,
    routers?: IEquipment[],
}

interface IProps {
    providerId: number,
    tag: string | string[],
    isAuth?: boolean,
    expand?: string,
}

type TDataResponse = TResponse<IBanner[], any>;

/**
 * @param providerId
 * @param isAuth
 * @param tag место размещения // must be an array
 * @param expand // must be an array of an enum (придумай сам что туда писать)
 */
export const getBanners = async({ providerId, isAuth = false, tag, expand = 'bannerInfo.tags,order' }: IProps): Promise<TDataResponse> => {
    try {
        if (!providerId) {
            throw new Error('Не передан домен для получения баннеров');
        }
        const tags = Array.isArray(tag) ? tag.join(',') : tag;
        const url = `/v2/banner-city/search?expand=${expand}&filter[tags]=${tags}&providerId=${providerId}&filter${isAuth
            ? '[available_auth]=1'
            : '[available_new]=1'
        }`;
        const response = await contentRequest.get(url);

        return {
            isSuccess: true,
            payload: response.data.items,
        };
    } catch (error) {
        return captureQueryError(error);
    }
};

const bannerMapper = (routers: IEquipment[]) => {
    const cache = new Map<number, IEquipment>();

    return function(banner: IBanner): IBanner {
        if (!banner.routers?.length) {
            return banner;
        }

        const result: IEquipment[] = [];

        for (const router of banner.routers) {
            const { id: routerId } = router;
            const cached = cache.get(routerId);
            const equipmentRouter = cached || routers.find(({ id }) => routerId === id);

            if (equipmentRouter) {
                const { id: key } = equipmentRouter;

                result.push(equipmentRouter);
                !cached && cache.set(key, equipmentRouter);
            }
        }

        return {
            ...banner,
            routers: result,
        };
    };
};

export const groupByTags = (banners: IBanner[], tags: string[], routers: IEquipment[]): Record<string, IBanner[]> => {
    const groups = tags.reduce((acc: Record<string, IBanner[]>, tag) => {
        acc[tag] = [];
        return acc;
    }, {});

    const mapper = bannerMapper(routers);

    return banners.reduce((acc, banner) => {
        const { bannerInfo: { tags: bannerTags } } = banner;

        tags.forEach(tag => {
            if (bannerTags.find(bannerTag => bannerTag.alias === tag)) {
                const mappedBanner = mapper(banner);

                acc[tag].push(mappedBanner);
            }
        });

        return acc;
    }, groups);
};
